import { create } from 'zustand';
import type { DashboardFilters, TabData } from '@/types/dashboard';

export interface ModalState {
  open: boolean;
  title: string;
  columns: string[];
  data: Record<string, unknown>[];
}

export interface TabConfig {
  icon: string;
  color: string;
}

export const TAB_CONFIG: Record<string, TabConfig> = {
  综合驾驶舱: { icon: '📊', color: 'blue' },
  每日出库: { icon: '🚚', color: 'blue' },
  进销存: { icon: '📦', color: 'gold' },
  三一工程: { icon: '🏪', color: 'purple' },
  车铺车销: { icon: '🚐', color: 'green' },
  校园压仓: { icon: '🎒', color: 'purple' },
  团队执行力: { icon: '👥', color: 'orange' },
  客户综合评估: { icon: '⚖️', color: 'red' },
  异常定位雷达: { icon: '雷达', color: 'red' },
};

export interface DashboardState {
  currentTab: string;
  filters: DashboardFilters;
  tabData: Record<string, TabData>;
  loading: boolean;
  error: string | null;
  globalPeriod: string | null;
  filterInfo: string | null;
  modal: ModalState;
  setCurrentTab: (tab: string) => void;
  setFilters: (filters: DashboardFilters) => void;
  setTabData: (tab: string, data: TabData) => void;
  /** 按块追加「加载更多」的行（每张明细表独立分页，block 形如 drill:0 / abnormal:2） */
  appendBlockRows: (tab: string, block: string, rows: Record<string, unknown>[], total: number) => void;
  /** 按块替换整批行（排序后重新加载） */
  replaceBlockRows: (tab: string, block: string, rows: Record<string, unknown>[], total: number) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setMeta: (globalPeriod: string, filterInfo: string) => void;
  openModal: (title: string, columns: string[], data: Record<string, unknown>[]) => void;
  closeModal: () => void;
}

export const useDashboardStore = create<DashboardState>((set) => ({
  currentTab: '综合驾驶舱',
  filters: {},
  tabData: {},
  loading: false,
  error: null,
  globalPeriod: null,
  filterInfo: null,
  modal: { open: false, title: '', columns: [], data: [] },
  setCurrentTab: (tab) => set({ currentTab: tab }),
  setFilters: (filters) => set({ filters }),
  setTabData: (tab, data) =>
    set((state) => ({
      tabData: { ...state.tabData, [tab]: data },
    })),
  appendBlockRows: (tab, block, rows, total) =>
    set((state) => {
      const old = state.tabData[tab];
      if (!old || rows.length === 0) return {};
      if (block.startsWith('drill:')) {
        const drills = (old.drills || []).map((d) =>
          d.block === block ? { ...d, data: [...d.data, ...rows], total } : d,
        );
        return { tabData: { ...state.tabData, [tab]: { ...old, drills } } };
      }
      const ab = old.abnormal;
      if (!ab) return {};
      // abnormal 数组形态：整块对应一张表
      if (Array.isArray(ab.data)) {
        if (ab.block !== block) return {};
        return {
          tabData: {
            ...state.tabData,
            [tab]: { ...old, abnormal: { ...ab, data: [...ab.data, ...rows], total } },
          },
        };
      }
      // abnormal dict 形态：反查 block_keys 找到对应子键
      const dict = ab.data as Record<string, Record<string, unknown>[]>;
      const subKey = Object.entries(ab.block_keys || {}).find(([, v]) => v === block)?.[0];
      if (!subKey) return {};
      return {
        tabData: {
          ...state.tabData,
          [tab]: {
            ...old,
            abnormal: {
              ...ab,
              data: { ...dict, [subKey]: [...(dict[subKey] || []), ...rows] },
              totals: { ...(ab.totals || {}), [subKey]: total },
            },
          },
        },
      };
    }),
  replaceBlockRows: (tab, block, rows, total) =>
    set((state) => {
      const old = state.tabData[tab];
      if (!old) return {};
      if (block.startsWith('drill:')) {
        const drills = (old.drills || []).map((d) =>
          d.block === block ? { ...d, data: rows, total } : d,
        );
        return { tabData: { ...state.tabData, [tab]: { ...old, drills } } };
      }
      const ab = old.abnormal;
      if (!ab) return {};
      if (Array.isArray(ab.data)) {
        if (ab.block !== block) return {};
        return {
          tabData: {
            ...state.tabData,
            [tab]: { ...old, abnormal: { ...ab, data: rows, total } },
          },
        };
      }
      const dict = ab.data as Record<string, Record<string, unknown>[]>;
      const subKey = Object.entries(ab.block_keys || {}).find(([, v]) => v === block)?.[0];
      if (!subKey) return {};
      return {
        tabData: {
          ...state.tabData,
          [tab]: {
            ...old,
            abnormal: {
              ...ab,
              data: { ...dict, [subKey]: rows },
              totals: { ...(ab.totals || {}), [subKey]: total },
            },
          },
        },
      };
    }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  setMeta: (globalPeriod, filterInfo) => set({ globalPeriod, filterInfo }),
  openModal: (title, columns, data) => set({ modal: { open: true, title, columns, data } }),
  closeModal: () => set((state) => ({ modal: { ...state.modal, open: false } })),
}));
